import { readMetaErrorCode } from './metaAdsConfig';

/**
 * ¿La API de este entorno soporta la fase 1 de Meta (público, plataformas, interacciones,
 * presupuesto, informe)? **Un solo lugar** para decidirlo: si cambia la regla, se cambia acá.
 *
 * Regla (confirmada por APIBOT, 27 oct): mostrar las pestañas nuevas solo si
 * `getmetacampaigns.budget_available === true` (estricto):
 *  - clave ausente → API sin fase 1 (sin `budget_available`) → ocultar;
 *  - `false` → código nuevo pero sin la migración 106 (o falla puntual de lectura) → ocultar;
 *  - `true` → todo listo → mostrar.
 * Una vez que dio `true`, se recuerda durante la sesión: si después una lectura falla puntualmente
 * y la API responde `false`, las pestañas no aparecen y desaparecen.
 *
 * Esto oculta solo lo nuevo: nunca la sección «Campañas» entera.
 */

/** Lee la señal de la respuesta cruda de `getmetacampaigns` (`data`). `null` = la API no la manda. */
export function readMetaFase1Signal(data) {
  return typeof data?.budget_available === 'boolean' ? data.budget_available : null;
}

/**
 * @param {{ budgetAvailable?: boolean|null }|null} campaignsState estado de `useMetaCampaigns`
 * @returns {boolean} solo `true` estricto
 */
export function metaFase1Supported(campaignsState) {
  return campaignsState?.budgetAvailable === true;
}

/**
 * Respuesta (envelope) de una ruta NUEVA de la fase 1 (`getmetaadsets`, `getmetaresults` con
 * secciones, `getcampaignbreakdowns` nuevo…) que significa «todavía no disponible en este entorno»:
 *  - error de red / CORS (la ruta no existe en el Gateway: sin cabeceras CORS) → sin `_httpStatus`;
 *  - 404 **sin** `code` (la Lambda vieja no conoce la ruta);
 *  - 503 `META_ADS_NOT_MIGRATED` (falta la migración 106).
 * Ocultan solo esa pantalla (o muestran «Todavía no disponible»), sin error rojo.
 * Un 404 con `code` (p. ej. `META_CAMPAIGN_NOT_FOUND`) es un error real: devuelve `false`.
 *
 * @param {object} res Envelope de `httpClient` / `safeRequest`, o un error de transporte.
 * @returns {boolean}
 */
export function isMetaFase1Unavailable(res) {
  if (!res || res._httpOk === true) return false;
  const status = res._httpStatus;
  if (status === null || status === undefined) return true;
  const code = readMetaErrorCode(res);
  if (status === 404) return !code;
  if (status === 503) return code === 'META_ADS_NOT_MIGRATED';
  return false;
}

// Memoria de la sesión (por espacio): evita el parpadeo si una lectura posterior devuelve `false`.
const sessionSupport = new Map();
const sessionKey = (ownerUserId) => String(ownerUserId || 'self');

/** Solo para tests. */
export function resetMetaFase1SessionCache() {
  sessionSupport.clear();
}

/**
 * Soporte de la fase 1 para el panel: `metaFase1Supported` + memoria de la sesión.
 * @param {object} campaignsState estado de `useMetaCampaigns`
 * @param {string|null} [ownerUserId]
 * @returns {boolean}
 */
export function useMetaFase1Support(campaignsState, ownerUserId = null) {
  const key = sessionKey(ownerUserId);
  const live = metaFase1Supported(campaignsState);
  if (live) sessionSupport.set(key, true);
  const supported = live || sessionSupport.get(key) === true;
  return supported;
}
